import React, { useContext, useMemo, useEffect, useState } from 'react'
import { Form, Row, Col, Dropdown, ButtonToolbar } from 'react-bootstrap'
import { DesignContext } from 'contexts/DesignContext'
import objectPath from 'object-path'
import colorContrast from 'color-contrast'
import { Tooltip } from '@mui/material'
import useAuth from 'hooks/useAuth'
import httpClient from 'utility/httpClient.util'
import Label from './Label'
import Icon from './Icon'
import { CustomButton } from './Button'
import { getShowUiConfig } from '../action/Config'
import { setApi } from 'designer/component/action/Api'
import {
  PRINT_URL,
  REPORT_CODE_PARAM,
  REPORT_CODE_PROP,
} from 'designer/component/config/Report'

const DEFAULT_DROPDOWN_CONFIG = {
  dataKey: '',
  mapAction: '',
  labelKey: 'label',
  valueKey: 'value',
  menuNameKey: 'reportName',
  menuIdKey: 'reportCode',
  reportCodeParamKey: REPORT_CODE_PARAM,
  parameterKey: 'params',
  iconKey: '',
  iconSourceKey: '',
  menuColorKey: '',
  menuTextColorKey: '',
  parameters: [],
}

const DEFAULT_DROPDOWN_API = {
  ...DEFAULT_DROPDOWN_CONFIG,
  service: '',
  endpoint: '',
  url: '',
  pathParams: [],
  queryParams: [],
  options: [],
  page: 1,
  perPage: 100,
}

const DEFAULT_MENU_COLOR = '#ffffff'

const DEFAULT_MENU_TEXT_COLOR = '#000000'

const getTextValue = (value) =>
  value === undefined || value === null || typeof value === 'object'
    ? ''
    : `${value}`

const getJsonData = (data) => {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data)
    } catch (error) {
      return []
    }
  }
  return data || []
}

const normalizeSourceData = (data) => {
  if (Array.isArray(data)) return data
  return []
}

const getDropdownMenuNameKey = (setting = {}) =>
  setting.menuNameKey || DEFAULT_DROPDOWN_CONFIG.menuNameKey

const getDropdownMenuIdKey = (setting = {}) =>
  setting.menuIdKey || DEFAULT_DROPDOWN_CONFIG.menuIdKey

const getDropdownReportCodeParamKey = (setting = {}) =>
  setting.reportCodeParamKey || DEFAULT_DROPDOWN_CONFIG.reportCodeParamKey

const getDropdownParameterKey = (setting = {}) =>
  setting.parameterKey || DEFAULT_DROPDOWN_CONFIG.parameterKey

const getSourceDataValue = (source, key) => {
  if (!key || !source || typeof source !== 'object') return undefined
  if (objectPath.has(source, key)) return objectPath.get(source, key)
  return objectPath.get(source, `value.${key}`)
}

const getSourceText = (source, keyList = []) => {
  let value = ''
  keyList.find((key) => {
    let sourceValue = getTextValue(getSourceDataValue(source, key))
    if (sourceValue) {
      value = sourceValue
      return true
    }
    return false
  })
  return value
}

const getSourceValue = (source, key) => {
  if (!key) return ''
  if (!source || typeof source !== 'object') {
    return ['label', 'value'].includes(key) ? getTextValue(source) : ''
  }
  return getTextValue(getSourceDataValue(source, key))
}

const createPrintQueryParam = ({ param, value, propType, key, test }) => {
  let paramValue = getTextValue(value)
  return {
    key: key || '',
    test: test !== undefined ? getTextValue(test) : paramValue,
    param: param || '',
    value: paramValue,
    propType: propType || 'custom',
  }
}

const createPrintConfigParam = (param, source, propType = 'input') =>
  createPrintQueryParam({
    param,
    key: source,
    value: source,
    propType,
    test: '',
  })

const createPrintParamFromObject = (paramData = {}, defaultParam) => {
  let param =
    paramData.param ||
    paramData.name ||
    paramData.key ||
    paramData.field ||
    defaultParam
  let propType = paramData.propType || 'input'

  if (paramData.literal !== undefined || propType === 'custom') {
    let value =
      paramData.literal !== undefined
        ? paramData.literal
        : paramData.value ?? paramData.test
    return createPrintQueryParam({
      param,
      value,
      propType: 'custom',
    })
  }

  let source = paramData.source || paramData.value || param
  return createPrintConfigParam(param, source, propType)
}

const normalizePrintParams = (params) => {
  if (Array.isArray(params)) {
    return params
      .map((paramData) => {
        if (typeof paramData === 'string') {
          return createPrintConfigParam(paramData, paramData)
        }
        if (!paramData || typeof paramData !== 'object') return null
        return createPrintParamFromObject(paramData)
      })
      .filter((paramData) => paramData?.param)
  }

  if (params && typeof params === 'object') {
    return Object.keys(params).map((param) => {
      let value = objectPath.get(params, param)
      if (value && typeof value === 'object') {
        return createPrintParamFromObject(value, param)
      }
      return createPrintConfigParam(param, value || param)
    })
  }

  return []
}

const normalizeDropdownParameter = (paramData = {}) => {
  let sourceParam = paramData.sourceParam || paramData.param
  return {
    ...paramData,
    sourceParam,
  }
}

const removeDropdownParameterSource = (paramData = {}) => {
  let { sourceParam, ...param } = paramData
  return param
}

const getParameterMatchName = (paramData = {}) =>
  getTextValue(paramData.sourceParam || paramData.param)

const getMappedPrintParams = (params, parameters = []) => {
  let parameterMap = {}
  parameters.forEach((paramData) => {
    let parameterName = getParameterMatchName(paramData)
    if (parameterName) {
      parameterMap[parameterName] = normalizeDropdownParameter(paramData)
    }
  })

  return normalizePrintParams(params).map((paramData) => {
    let parameter = normalizeDropdownParameter(paramData)
    let parameterName = getParameterMatchName(parameter)
    let mappedParameter = parameterMap[parameterName]
    if (!mappedParameter) return removeDropdownParameterSource(parameter)
    return removeDropdownParameterSource({
      ...parameter,
      ...mappedParameter,
      sourceParam: parameterName,
    })
  })
}

const createDropdownPrintAction = (source, setting) => {
  let params = getSourceDataValue(source, getDropdownParameterKey(setting))
  let reportCode = getSourceValue(
    source,
    getDropdownReportCodeParamKey(setting)
  )
  let queryParams = [
    createPrintQueryParam({
      param: REPORT_CODE_PARAM,
      value: reportCode,
      propType: REPORT_CODE_PROP,
    }),
    ...getMappedPrintParams(params, setting.parameters || []),
  ]

  return {
    event: 'onClick',
    name: 'print',
    print: {
      url: PRINT_URL,
      pathParams: [],
      queryParams,
    },
  }
}

const getSourceMenuData = (source, setting, index) => {
  if (setting.mapAction === 'print') {
    let reportCode =
      getSourceValue(source, getDropdownMenuIdKey(setting)) ||
      getSourceText(source, ['code']) ||
      getSourceValue(source, setting.valueKey) ||
      getSourceText(source, ['label']) ||
      `Menu ${index + 1}`
    let reportName =
      getSourceValue(source, getDropdownMenuNameKey(setting)) ||
      getSourceValue(source, setting.labelKey) ||
      getSourceText(source, ['name', 'label', 'title']) ||
      reportCode

    return {
      label: reportName,
      value: reportCode,
      action: createDropdownPrintAction(source, setting),
    }
  }

  let label =
    getSourceValue(source, getDropdownMenuNameKey(setting)) ||
    getSourceValue(source, setting.labelKey) ||
    getSourceValue(source, setting.valueKey) ||
    `Menu ${index + 1}`
  let value = getSourceValue(source, getDropdownMenuIdKey(setting)) || label

  return {
    label,
    value,
    action: { event: 'onClick', name: '' },
  }
}

const createDropdownMenuList = (data, setting) => {
  return normalizeSourceData(data).map((source, index) => {
    let { label, value, action } = getSourceMenuData(source, setting, index)
    let menuId = getTextValue(value || label) || `Menu ${index + 1}`
    let icon = getSourceValue(source, setting.iconKey)
    let iconSource =
      getSourceValue(source, setting.iconSourceKey) || (icon ? 'feather' : '')
    let menuColor =
      getSourceValue(source, setting.menuColorKey) || DEFAULT_MENU_COLOR
    let menuTextColor =
      getSourceValue(source, setting.menuTextColorKey) ||
      DEFAULT_MENU_TEXT_COLOR

    return {
      key: `dropdown-${menuId}-${index + 1}`,
      label,
      value,
      icon,
      iconSource,
      menuColor,
      menuTextColor,
      action,
      isDisabled: false,
      '@value': source,
    }
  })
}

const ButtonDropdown = (props) => {
  let { uischema = {}, disabled = false, label } = props

  const { config: authConfig = [] } = useAuth()
  const designContext = useContext(DesignContext)
  const { jsonForm, setFormData, elementAction } = designContext
  const { formData = {} } = jsonForm
  const {
    isDynamic = false,
    dynamicName = '',
    dynamicIndex = 0,
    isArray,
    arrayName,
  } = props?.config || {}

  const fieldName =
    props?.fieldName ||
    (isArray
      ? `@${arrayName}.${uischema.name}`
      : isDynamic
      ? `${dynamicName}.${dynamicIndex}.${uischema.name}`
      : uischema.name)
  const fieldKey =
    props?.fieldKey ||
    (isArray
      ? `@${arrayName}.${uischema.key}`
      : isDynamic
      ? `${dynamicName}.${dynamicIndex}.${uischema.key}`
      : uischema.key)

  const buttonKey = uischema.key
  const config = useMemo(
    () => (Array.isArray(authConfig) ? authConfig : []),
    [authConfig]
  )
  const dropdownSource = uischema.dropdownSource || 'custom'
  const customDropdownList = uischema.dropdownList || []
  const dropdownConfigSetting = useMemo(
    () => ({
      ...DEFAULT_DROPDOWN_CONFIG,
      ...(uischema.dropdownConfig || {}),
    }),
    [uischema.dropdownConfig]
  )
  const dropdownApiSetting = useMemo(
    () => ({
      ...DEFAULT_DROPDOWN_API,
      ...(uischema.dropdownApi || {}),
    }),
    [uischema.dropdownApi]
  )
  const [apiDropdownOptions, setApiDropdownOptions] = useState(
    dropdownApiSetting.options || []
  )
  const isShowUi = getShowUiConfig({
    uischema,
    config,
    jsonForm,
  })
  const hasEnable = objectPath.has(elementAction, ['isEnable', buttonKey])
  const isEnable = objectPath.get(elementAction, ['isEnable', buttonKey])
  const isLocked = objectPath.get(elementAction, ['isLockedEdit'])
  const [styleChange, setStyleChange] = useState({})

  const [buttonText, buttonType, buttonColor] = useMemo(() => {
    return [
      uischema.buttonText,
      uischema.buttonType,
      uischema.buttonColor || '#4099ff',
    ]
  }, [uischema.buttonText, uischema.buttonType, uischema.buttonColor])

  const getConfigData = (setting) => {
    let foundConfig = config.find(
      ({ configCode }) => configCode === setting.configCode
    )
    if (!foundConfig) return []
    let data = getJsonData(foundConfig?.jsonData ?? foundConfig?.data)
    if (setting.dataKey) {
      data = objectPath.get(data, setting.dataKey) || []
    }
    return normalizeSourceData(data)
  }

  const createApiUrl = (setting) => {
    let api = setApi(
      {
        ...setting,
        apiType: 'internal',
        isOption: true,
        jsonForm,
      },
      formData
    )
    return api || `${setting.service || ''}${setting.endpoint || ''}`
  }

  const getDropdownApiSettingWithUrl = (setting = {}) => {
    let nextSetting = {
      ...DEFAULT_DROPDOWN_API,
      ...(setting || {}),
    }
    let hasApiSource = nextSetting.service || nextSetting.endpoint
    if (!nextSetting.url && hasApiSource) {
      objectPath.set(nextSetting, 'url', createApiUrl(nextSetting))
    }
    return nextSetting
  }

  const configDropdownList = useMemo(() => {
    if (dropdownSource !== 'config') return []
    let configData = getConfigData(dropdownConfigSetting)
    return createDropdownMenuList(configData, dropdownConfigSetting)
  }, [config, dropdownConfigSetting, dropdownSource])

  useEffect(() => {
    if (dropdownSource !== 'api') return

    let setting = getDropdownApiSettingWithUrl(dropdownApiSetting)
    if (!setting.url) {
      setApiDropdownOptions(setting.options || [])
      return
    }

    let isActive = true
    httpClient
      .get(setting.url)
      .then(({ data }) => {
        if (!isActive) return
        let optionData = setting.dataKey
          ? objectPath.get(data, setting.dataKey)
          : data
        setApiDropdownOptions(normalizeSourceData(optionData))
      })
      .catch(() => {
        if (!isActive) return
        setApiDropdownOptions(setting.options || [])
      })

    return () => {
      isActive = false
    }
  }, [dropdownSource, dropdownApiSetting, formData])

  const apiDropdownList = useMemo(() => {
    if (dropdownSource !== 'api') return []
    return createDropdownMenuList(apiDropdownOptions, dropdownApiSetting)
  }, [apiDropdownOptions, dropdownApiSetting, dropdownSource])

  const dropdownList = useMemo(() => {
    if (dropdownSource === 'config') {
      return configDropdownList
    }
    if (dropdownSource === 'api') {
      return apiDropdownList
    }
    return customDropdownList
  }, [apiDropdownList, configDropdownList, customDropdownList, dropdownSource])

  const isReadOnly = useMemo(() => {
    if (isLocked) return isLocked
    if (hasEnable) {
      return isEnable === false ? true : false
    }
    return false
  }, [hasEnable, isEnable, isLocked])

  const isDisable = useMemo(() => {
    if (hasEnable) {
      return isEnable === false ? true : false
    }
    return disabled
  }, [buttonKey, disabled, hasEnable, isEnable])

  const getLightDarkColor = (col, amt) => {
    let usePound = false
    if (col[0] == '#') {
      col = col.slice(1)
      usePound = true
    }
    let num = parseInt(col, 16)
    let r = (num >> 16) + amt
    if (r > 255) r = 255
    else if (r < 0) r = 0
    let b = ((num >> 8) & 0x00ff) + amt
    if (b > 255) b = 255
    else if (b < 0) b = 0
    let g = (num & 0x0000ff) + amt
    if (g > 255) g = 255
    else if (g < 0) g = 0
    return (usePound ? '#' : '') + (g | (b << 8) | (r << 16)).toString(16)
  }

  const getColorText = (color) => {
    let contrast = colorContrast('#fff', color)
    if (contrast >= 1.5) {
      return '#fff'
    }
    return '#000'
  }

  const getColorHover = (color) => {
    let contrast = colorContrast('#fff', color)
    if (contrast >= 2) {
      return getLightDarkColor(color, 20)
    }
    return getLightDarkColor(color, -20)
  }

  const styles = {
    default: {
      color: getColorText(buttonColor),
      borderColor: buttonColor,
      backgroundColor: buttonColor,
    },
    defaultHover: {
      borderColor: getColorHover(buttonColor),
      backgroundColor: getColorHover(buttonColor),
    },
    outline: {
      color: buttonColor,
      borderColor: buttonColor,
      backgroundColor: 'transparent',
    },
    outlineHover: {
      color: getColorText(buttonColor),
      borderColor: buttonColor,
      backgroundColor: buttonColor,
    },
    icon: {
      color: buttonColor,
      borderColor: buttonColor,
      backgroundColor: 'transparent',
    },
    left: {
      marginLeft: '0',
      marginRight: 'auto',
    },
    center: {
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    right: {
      marginLeft: 'auto',
      marginRight: '0',
    },
  }

  const wrapperStyle = useMemo(() => {
    if (uischema.buttonPosition === 'center') return styles.center
    if (uischema.buttonPosition === 'right') return styles.right
    return styles.left
  }, [uischema.buttonPosition])

  const handleHover = (isEnter) => {
    if (buttonType === 'icon') {
      setStyleChange(styles.icon)
      return
    }
    if (buttonType === 'outline') {
      setStyleChange(isEnter ? styles.outlineHover : styles.outline)
    } else {
      setStyleChange(isEnter ? styles.defaultHover : styles.default)
    }
  }

  const getMenuActionUi = (menuItem, index) => {
    let menuKey = menuItem.key || `${buttonKey}-dropdown-${index + 1}`
    return {
      ...uischema,
      tagname: 'button',
      key: menuKey,
      name: menuItem.value || menuItem.label || menuKey,
      action: menuItem.action || {},
      beforeAction: menuItem.beforeAction || {},
      afterSuccess: menuItem.afterSuccess || {},
      afterAction: menuItem.afterAction || {},
      buttonText: menuItem.label || menuItem.value || 'Menu',
      buttonIcon: menuItem.icon || uischema.buttonIcon,
      buttonIconSource: menuItem.iconSource || uischema.buttonIconSource,
    }
  }

  const hasMenuAction = (menuItem) => {
    return objectPath.get(menuItem, 'action.name')
  }

  const handleSelect = (menuItem, doAction) => {
    let value = menuItem.value || menuItem.label || ''
    objectPath.set(formData, buttonKey, value)
    if (fieldName) {
      objectPath.set(formData, fieldName, value)
    }
    setFormData(formData)
    if (doAction) {
      doAction()
    }
  }

  const renderDropdownItem = (menuItem, index, actionProps = {}) => {
    return (
      <Dropdown.Item
        key={menuItem.key || `dropdown-menu-${index + 1}`}
        eventKey={menuItem.value || `${index + 1}`}
        disabled={menuItem.isDisabled || actionProps.disabled}
        onClick={() => handleSelect(menuItem, actionProps.onClick)}
        style={{
          backgroundColor: menuItem.menuColor || 'transparent',
          color: menuItem.menuTextColor,
          margin: 0,
          padding: '.5rem .75rem',
        }}
      >
        {menuItem.icon && (
          <span className="mr-1">
            <Icon iconName={menuItem.icon} />
          </span>
        )}
        {menuItem.label || menuItem.value || 'Menu'}
      </Dropdown.Item>
    )
  }

  useEffect(() => {
    if (buttonType === 'outline') {
      setStyleChange(styles.outline)
    } else if (buttonType === 'icon') {
      setStyleChange(styles.icon)
    } else {
      setStyleChange(styles.default)
    }
  }, [buttonColor, buttonType, uischema.buttonWidth])

  if (isLocked) {
    return <></>
  }

  return (
    <Form.Group
      className="mb-0"
      controlId={uischema.controlId}
      hidden={!isShowUi}
    >
      <Row className="w-100 mx-0">
        <Label
          {...props}
          uischema={uischema}
          label={label}
          isEnable={!isReadOnly}
        />
        <Col sm={uischema.elementWidth} className="p-0 mt-1 d-flex">
          <ButtonToolbar style={wrapperStyle}>
            <Dropdown drop={uischema.dropdownDirection ?? 'down'}>
              <Dropdown.Toggle
                id={fieldKey}
                name={fieldName}
                style={{ ...styleChange, width: uischema.buttonWidth }}
                disabled={isDisable}
                onMouseEnter={() => handleHover(true)}
                onMouseLeave={() => handleHover(false)}
              >
                <span
                  style={
                    uischema.buttonIconSource === 'feather'
                      ? {
                          top: '-1px',
                          position: 'relative',
                        }
                      : {}
                  }
                  className={buttonText && 'mr-1'}
                >
                  <Icon iconName={uischema.buttonIcon} />
                </span>
                {buttonType !== 'icon' && buttonText}
              </Dropdown.Toggle>
              <Dropdown.Menu
                style={{
                  backgroundColor: 'transparent',
                  border: 0,
                  boxShadow: 'none',
                  padding: 0,
                }}
              >
                {dropdownList.length === 0 && (
                  <Dropdown.Item
                    disabled
                    style={{
                      backgroundColor: 'transparent',
                    }}
                  >
                    No menu
                  </Dropdown.Item>
                )}
                {dropdownList.map((menuItem, index) => {
                  if (!hasMenuAction(menuItem)) {
                    return renderDropdownItem(menuItem, index)
                  }
                  return (
                    <CustomButton
                      {...props}
                      key={menuItem.key || `dropdown-menu-${index + 1}`}
                      uischema={getMenuActionUi(menuItem, index)}
                      disabled={disabled || menuItem.isDisabled}
                      actionOnly
                      renderAction={({ onClick, disabled: actionDisabled }) =>
                        renderDropdownItem(menuItem, index, {
                          onClick,
                          disabled: actionDisabled,
                        })
                      }
                    />
                  )
                })}
              </Dropdown.Menu>
            </Dropdown>
          </ButtonToolbar>
        </Col>
      </Row>
    </Form.Group>
  )
}

const OverlayButtonDropdown = (props) => {
  let { uischema } = props
  let tooltipText = objectPath.get(uischema, 'action.tooltipText')

  if (tooltipText) {
    return (
      <Tooltip
        title={<span style={{ fontSize: '14px' }}>{tooltipText}</span>}
        arrow
      >
        <span>
          <ButtonDropdown {...props} />
        </span>
      </Tooltip>
    )
  }

  return <ButtonDropdown {...props} />
}

export default OverlayButtonDropdown
